/**
 * Add food: describe it, snap a photo, or enter it by hand.
 * AI results come back as an editable draft; nothing is saved until "Save".
 */
import { useMemo, useRef, useState, type ReactElement } from 'react';
import { addNutrients, scaleNutrients } from '@engine/nutrients';
import { effectiveLayout, foodBlocks, storageFor } from '../lib/diaryLayout';
import { itemNutrients, roundMap } from '../lib/foodNutrients';
import { macroCalorieGap } from '../lib/labelCheck';
import { errorMessage, log } from '../lib/logger';
import { MEAL_HOUR } from '../lib/labels';
import { prepareImage, type PreparedImage } from '../lib/image';
import { timestampForDay } from '../lib/dates';
import type { AnalyzeMealResponse, DiaryBlock, MealInsert, MealItem, MealSource } from '../lib/types';
import { useApp } from '../state/app';
import { BarcodeLookup } from './BarcodeLookup';
import { FoodDetail } from './FoodDetail';
import { FoodSearch } from './FoodSearch';
import { Icon } from './Icon';
import { Modal, Spinner } from './ui';

type Tab = 'search' | 'barcode' | 'text' | 'photo' | 'manual';

interface Draft {
  description: string;
  /** Diary section id (diet_settings.diary_layout). */
  section: string;
  source: MealSource;
  items: MealItem[];
  ai_model: string | null;
  ai_confidence: number | null;
}

/** Items picked from the food database carry their full profile; their numbers follow the amount. */
const isLinked = (it: MealItem): boolean => it.food_id !== undefined && it.nutrients !== undefined && it.grams > 0;

function rescaleLinked(it: MealItem, patch: Partial<MealItem>): MealItem {
  if (!isLinked(it) || (patch.grams === undefined && patch.quantity === undefined)) return { ...it, ...patch };
  let grams: number = Math.max(0.1, patch.grams ?? it.grams);
  if (patch.quantity !== undefined && patch.grams === undefined && it.quantity > 0) grams = (it.grams / it.quantity) * patch.quantity;
  // scaleNutrients(m, g) multiplies by g / 100, so g = 100 * 100 / grams gives the per-100 g profile.
  const per100 = scaleNutrients(it.nutrients!, 10_000 / it.grams);
  const n = roundMap(scaleNutrients(per100, grams));
  const g = (k: string): number => n[k] ?? 0;
  return {
    ...it, ...patch, grams: Math.round(grams * 10) / 10, nutrients: n,
    calories: Math.round(g('energy_kcal') * 10) / 10, protein_g: g('protein_g'), carbs_g: g('carbs_g'), fat_g: g('fat_g'),
    fiber_g: g('fiber_g'), sugar_g: g('sugars_g'), alcohol_g: g('alcohol_g'), sodium_mg: g('sodium_mg'), potassium_mg: g('potassium_mg'),
  };
}

const blankItem = (): MealItem => ({ name: '', quantity: 1, unit: 'serving', grams: 0, calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0, sugar_g: 0, alcohol_g: 0, sodium_mg: 0, potassium_mg: 0 });
const r1 = (x: number): number => Math.round(x * 10) / 10;

type NumKey = 'quantity' | 'grams' | 'calories' | 'protein_g' | 'carbs_g' | 'fat_g' | 'fiber_g';
const NUM_COLS: ReadonlyArray<{ key: NumKey; label: string }> = [
  { key: 'quantity', label: 'Amount' }, { key: 'grams', label: 'Grams' }, { key: 'calories', label: 'Calories' },
  { key: 'protein_g', label: 'Protein g' }, { key: 'carbs_g', label: 'Carbs g' }, { key: 'fat_g', label: 'Fat g' }, { key: 'fiber_g', label: 'Fiber g' },
];

export function AddFoodModal({ day, defaultSectionId, initialTab = 'search', onClose, onSaved }: { day: string; defaultSectionId: string; initialTab?: 'search' | 'barcode' | 'text' | 'photo'; onClose: () => void; onSaved: () => void }): ReactElement {
  const { source, today, toast, settings } = useApp();
  const sections: DiaryBlock[] = foodBlocks(effectiveLayout(settings.diary_layout, settings.energy_mode));
  const [tab, setTab] = useState<Tab>(initialTab);
  const [sectionId, setSectionId] = useState<string>(sections.some((b) => b.id === defaultSectionId) ? defaultSectionId : sections[0]?.id ?? 'snack');
  const sectionOf = (id: string): DiaryBlock => sections.find((b) => b.id === id) ?? { id, kind: 'food', label: id };
  const [text, setText] = useState<string>('');
  const [photo, setPhoto] = useState<PreparedImage | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeMealResponse | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  /** 'input' = search/describe/photo; 'review' = the editable item table. */
  const [view, setView] = useState<'input' | 'review'>('input');
  const [picked, setPicked] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const totals = useMemo(() => {
    const items: MealItem[] = draft?.items ?? [];
    const s = (k: keyof MealItem): number => r1(items.reduce((a, it) => a + (Number(it[k]) || 0), 0));
    return {
      calories: s('calories'), protein_g: s('protein_g'), carbs_g: s('carbs_g'), fat_g: s('fat_g'),
      fiber_g: s('fiber_g'), sugar_g: s('sugar_g'), alcohol_g: s('alcohol_g'), sodium_mg: s('sodium_mg'), potassium_mg: s('potassium_mg'),
    };
  }, [draft]);

  const analyze = async (): Promise<void> => {
    setError(null);
    const hasText: boolean = text.trim().length > 0;
    if (tab === 'text' && !hasText) { setError('Describe what you ate first, e.g. "3 eggs, 2 slices toast, black coffee".'); return; }
    if (tab === 'photo' && !photo) { setError('Choose or take a photo first.'); return; }
    setBusy(true);
    try {
      const res: AnalyzeMealResponse = await source.analyzeMeal({
        text: hasText ? text.trim() : undefined,
        image: tab === 'photo' && photo ? { data: photo.dataUrl, mimeType: photo.mimeType } : undefined,
        mealType: storageFor(sectionOf(sectionId)).meal_type,
      });
      setAnalysis(res);
      if (res.isFood && res.meal) {
        const meal = res.meal;
        setDraft((d) => d
          ? { ...d, items: [...d.items, ...meal.items.map((it) => ({ ...it }))], description: d.description || meal.description }
          : {
            description: meal.description, section: sectionId, source: meal.source,
            items: meal.items.map((it) => ({ ...it })), ai_model: meal.ai_model, ai_confidence: meal.ai_confidence,
          });
        setView('review');
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const startManual = (): void => {
    setTab('manual');
    setAnalysis(null);
    setError(null);
    setDraft((d) => d ? { ...d, items: [...d.items, blankItem()] } : { description: '', section: sectionId, source: 'manual', items: [blankItem()], ai_model: null, ai_confidence: null });
    setView('review');
  };

  /** A food picked from the database (or scanned) joins the draft with its full nutrient profile. */
  const addFromDatabase = (item: MealItem, from: MealSource = 'usda'): void => {
    setDraft((d) => d
      ? { ...d, items: [...d.items, item], source: d.items.length === 0 ? from : d.source }
      : { description: '', section: sectionId, source: from, items: [item], ai_model: null, ai_confidence: null });
    setPicked(null);
    setError(null);
    setView('review');
    toast(`Added ${item.name.split(',')[0]}`);
  };

  const onPickFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setError(null);
    try { setPhoto(await prepareImage(file)); } catch (err) { setError(errorMessage(err)); }
  };

  const setItem = (i: number, patch: Partial<MealItem>): void => {
    setDraft((d) => (d ? { ...d, items: d.items.map((it, j) => (j === i ? rescaleLinked(it, patch) : it)) } : d));
  };

  const save = async (): Promise<void> => {
    if (!draft) return;
    const items: MealItem[] = draft.items.filter((it) => it.name.trim().length > 0 || it.calories > 0);
    const description: string = draft.description.trim() || items.map((it) => it.name).filter(Boolean).join(', ');
    if (description.length === 0) { setError('Give the meal a name or add at least one food.'); return; }
    if (totals.fiber_g > totals.carbs_g) { setError('Fiber can’t be more than total carbs (carbs here are total carbohydrate, like a US label).'); return; }
    const nutrients = roundMap(addNutrients(...items.map(itemNutrients)));
    const section: DiaryBlock = sectionOf(draft.section);
    const where = storageFor(section);
    const row: MealInsert = {
      eaten_at: day === today ? new Date().toISOString() : timestampForDay(day, MEAL_HOUR[where.meal_type]),
      local_date: day,
      meal_type: where.meal_type,
      diary_section: where.diary_section,
      description: description.slice(0, 2000),
      source: draft.source,
      ...totals,
      nutrients,
      items: items.map((it) => ({ ...it, name: it.name.trim() || 'Unnamed item' })),
      ai_model: draft.ai_model,
      ai_confidence: draft.ai_confidence,
    };
    setBusy(true);
    try {
      await source.insertMeal(row);
      toast(`Saved ${Math.round(row.calories)} kcal to ${section.label}`);
      onSaved();
      onClose();
    } catch (err) {
      log.error('add_food.save_failed', { error: errorMessage(err) });
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const footer: ReactElement = view === 'review' && draft ? (
    <>
      <button type="button" className="btn" onClick={() => { setDraft(null); setAnalysis(null); setView('input'); }} disabled={busy}>Start over</button>
      <button type="button" className="btn" onClick={() => { setTab('search'); setView('input'); }} disabled={busy}><Icon name="plus" size={16} /> Add more</button>
      <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy || draft.items.length === 0}>{busy ? <Spinner /> : <Icon name="check" size={18} />} Save to diary</button>
    </>
  ) : (
    <>
      {draft && <button type="button" className="btn" onClick={() => setView('review')} style={{ marginRight: 'auto' }}>Back to {draft.items.length} item{draft.items.length === 1 ? '' : 's'}</button>}
      <button type="button" className="btn" onClick={onClose}>Cancel</button>
      {(tab === 'text' || tab === 'photo') && <button type="button" className="btn btn-primary" onClick={() => void analyze()} disabled={busy}>{busy ? <Spinner /> : <Icon name="sparkle" size={18} />} {busy ? 'Analyzing…' : 'Analyze'}</button>}
    </>
  );

  return (
    <Modal title="Add food" onClose={onClose} footer={footer} width={820}>
      {view === 'input' && (
        <>
          <div className="tabs" role="tablist">
            <button type="button" className={`tab ${tab === 'search' ? 'on' : ''}`} onClick={() => { setTab('search'); setPicked(null); }}><Icon name="search" size={16} /> Search</button>
            <button type="button" className={`tab ${tab === 'barcode' ? 'on' : ''}`} onClick={() => { setTab('barcode'); setPicked(null); }}><Icon name="barcode" size={16} /> Scan</button>
            <button type="button" className={`tab ${tab === 'text' ? 'on' : ''}`} onClick={() => setTab('text')}><Icon name="text" size={16} /> Describe it</button>
            <button type="button" className={`tab ${tab === 'photo' ? 'on' : ''}`} onClick={() => setTab('photo')}><Icon name="camera" size={16} /> Photo</button>
            <button type="button" className={`tab ${tab === 'manual' ? 'on' : ''}`} onClick={startManual}><Icon name="edit" size={16} /> Manual</button>
          </div>
          {tab === 'search' && (
            picked === null
              ? <FoodSearch autoFocus onPick={(h) => setPicked(h.id)} />
              : (
                <div className="stack" style={{ gap: 12 }}>
                  <button type="button" className="btn btn-sm btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setPicked(null)}><Icon name="chevL" size={16} /> Back to results</button>
                  <FoodDetail foodId={picked} onAdd={(it) => addFromDatabase(it)} addLabel="Add to this meal" />
                </div>
              )
          )}
          {tab === 'barcode' && (
            picked === null
              ? <BarcodeLookup onFood={setPicked} />
              : (
                <div className="stack" style={{ gap: 12 }}>
                  <button type="button" className="btn btn-sm btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setPicked(null)}><Icon name="barcode" size={16} /> Scan another</button>
                  <FoodDetail foodId={picked} onAdd={(it) => addFromDatabase(it, 'barcode')} addLabel="Add to this meal" />
                </div>
              )
          )}
          {tab !== 'search' && tab !== 'barcode' && <div className="stack" style={{ gap: 16 }}>
            <label className="field">
              <span>Diary section</span>
              <select className="input" value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
                {sections.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </label>
            {tab === 'photo' && (
              <div className="drop" onClick={() => fileRef.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') fileRef.current?.click(); }}>
                {photo ? <img src={photo.dataUrl} alt="Meal to analyze" /> : (
                  <div className="muted"><Icon name="camera" size={34} /><div style={{ fontWeight: 700, marginTop: 8 }}>Tap to take or choose a photo</div><small className="faint">JPEG, PNG or WebP · resized before upload</small></div>
                )}
                <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void onPickFile(e.target.files?.[0])} />
              </div>
            )}
            <label className="field">
              <span>{tab === 'photo' ? 'Anything the photo can’t show? (optional)' : 'What did you eat?'}</span>
              <textarea className="input" value={text} maxLength={2000} placeholder={tab === 'photo' ? 'e.g. cooked in 1 tbsp butter, about 8 oz steak' : 'e.g. 3 scrambled eggs, 2 slices whole wheat toast with butter, black coffee'} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void analyze(); }} />
              <small>Amounts help a lot: “8 oz”, “1.5 cups”, “2 slices”. Ctrl+Enter to analyze.</small>
            </label>
          </div>}
        </>
      )}

      {error !== null && <div className="note err" style={{ marginTop: 14 }}>{error}</div>}

      {analysis && !analysis.isFood && (
        <div className="note warn" style={{ marginTop: 14 }}>
          That didn’t look like food I could estimate.{analysis.clarifyingQuestion ? ` ${analysis.clarifyingQuestion}` : ''}
          {analysis.warnings.length > 0 && <ul>{analysis.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
        </div>
      )}

      {view === 'review' && draft && (
        <div className="stack" style={{ gap: 16 }}>
          <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr' }}>
            <label className="field"><span>Name</span><input className="input" value={draft.description} maxLength={2000} placeholder="e.g. Post-workout shake" onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></label>
            <label className="field"><span>Diary section</span>
              <select className="input" value={draft.section} onChange={(e) => setDraft({ ...draft, section: e.target.value })}>
                {sections.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </label>
          </div>

          {analysis && (analysis.assumptions.length > 0 || analysis.warnings.length > 0 || analysis.clarifyingQuestion) && (
            <div className="note info">
              <strong><Icon name="info" size={15} /> Check these before saving</strong>
              <ul>
                {analysis.clarifyingQuestion && <li>{analysis.clarifyingQuestion}</li>}
                {analysis.assumptions.map((a) => <li key={`a-${a}`}>{a}</li>)}
                {analysis.warnings.map((w) => <li key={`w-${w}`}>{w}</li>)}
              </ul>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table className="draft-table">
              <thead>
                <tr><th>Food</th><th>Unit</th>{NUM_COLS.map((c) => <th key={c.key}>{c.label}</th>)}<th /></tr>
              </thead>
              <tbody>
                {draft.items.map((it, i) => (
                  <tr key={i}>
                    <td className="name"><input value={it.name} placeholder="Food name" onChange={(e) => setItem(i, { name: e.target.value })} aria-label="Food name" /></td>
                    <td className="unit"><input value={it.unit} title={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })} aria-label="Unit" /></td>
                    {NUM_COLS.map((c) => (
                      <td key={c.key}><input type="number" min={0} step="any" inputMode="decimal" value={Number(it[c.key] ?? 0)} aria-label={c.label}
                        readOnly={isLinked(it) && c.key !== 'quantity' && c.key !== 'grams'}
                        title={isLinked(it) && c.key !== 'quantity' && c.key !== 'grams' ? 'From the food database: change the amount instead' : undefined}
                        onChange={(e) => setItem(i, { [c.key]: Math.max(0, Number(e.target.value) || 0) } as Partial<MealItem>)} /></td>
                    ))}
                    <td><button type="button" className="icon-btn" aria-label="Remove food" onClick={() => setDraft({ ...draft, items: draft.items.filter((_, j) => j !== i) })}><Icon name="trash" size={16} /></button></td>
                  </tr>
                ))}
                <tr className="draft-total">
                  <td colSpan={4}>Total</td>
                  <td className="num">{Math.round(totals.calories)}</td>
                  <td className="num" style={{ color: 'var(--protein)' }}>{totals.protein_g}</td>
                  <td className="num" style={{ color: 'var(--carbs)' }}>{totals.carbs_g}</td>
                  <td className="num" style={{ color: 'var(--fat)' }}>{totals.fat_g}</td>
                  <td className="num">{totals.fiber_g}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
          {(() => {
            const off = draft.items
              .map((it, i) => ({ it, i, gap: isLinked(it) ? null : macroCalorieGap(it) }))
              .filter((x) => x.gap?.flagged);
            return off.length > 0 ? (
              <div className="note warn">
                <b>These calories don’t match the macros</b> (protein and carbs 4 kcal/g, fiber 2, fat 9, alcohol 7):
                <ul>{off.map(({ it, i, gap }) => <li key={i}>{it.name || `Row ${i + 1}`}: {Math.round(it.calories)} kcal typed, macros add up to {Math.round(gap!.macroKcal)} kcal.</li>)}</ul>
              </div>
            ) : null;
          })()}
          <div className="row">
            <button type="button" className="btn btn-sm" onClick={() => setDraft({ ...draft, items: [...draft.items, blankItem()] })}><Icon name="plus" size={16} /> Add a food</button>
            {draft.ai_model && <span className="faint" style={{ fontSize: 12.5, marginLeft: 'auto' }}>Estimated by {draft.ai_model}{draft.ai_confidence !== null ? ` · confidence ${Math.round(draft.ai_confidence * 100)}%` : ''}. Edit any number before saving.</span>}
          </div>
        </div>
      )}
    </Modal>
  );
}
